import { type LucideIcon } from "lucide-react"
import { Link, useLocation } from "react-router"

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import {
  SidebarGroup,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  sidebarCollapseChevronClassName,
  sidebarCollapseLabelClassName,
  useSidebar,
} from "@/components/ui/sidebar"
import { SidebarChevronIcon } from "@/components/sidebar/sidebar-icons"
import { cn } from "@/lib/utils"

function pathMatchesItem(pathname: string, url: string): boolean {
  const base = url.split('?')[0]
  if (base === '/') return pathname === '/'
  return pathname === base || pathname.startsWith(`${base}/`)
}

/** Exact match for submenu rows (path + query), so siblings don't all look active. */
function pathMatchesSubItem(pathname: string, search: string, url: string): boolean {
  const [path, query = ''] = url.split('?')
  if (pathname !== path) return false
  if (!query) return search === '' || search === '?'
  const current = new URLSearchParams(search)
  const expected = new URLSearchParams(query)
  for (const [key, value] of expected.entries()) {
    if (current.get(key) !== value) return false
  }
  return true
}

type NavIcon = LucideIcon | ((props: { className?: string }) => React.ReactNode)

type NavItem = {
  title: string
  url: string
  icon: NavIcon
  isActive?: boolean
  featured?: boolean
  /** Count shown next to the title, for example open approvals. Hidden when 0. */
  badge?: number
  items?: {
    title: string
    url: string
    main?: boolean
  }[]
}

function getMainSubItemUrl(item: NavItem): string {
  const mainSubItem = item.items?.find((subItem) => subItem.main)
  return mainSubItem?.url ?? item.items?.[0]?.url ?? item.url
}

export function NavMain({
  items,
}: {
  items: NavItem[]
}) {
  const { pathname, search } = useLocation()
  const { state, isMobile } = useSidebar()
  const goToMainOnParentClick = state === 'collapsed' && !isMobile

  return (
    <SidebarGroup className="p-2 pt-1">
      <SidebarMenu className="gap-1.5">
        {items.map((item) => {
          const hasChildren = Boolean(item.items?.length)
          const hasActiveChild = item.items?.some((subItem) =>
            pathMatchesItem(pathname, subItem.url),
          )
          const active = hasChildren
            ? Boolean(hasActiveChild)
            : pathMatchesItem(pathname, item.url)
          const Icon = item.icon

          const buttonClassName = cn(
            "h-[35px] rounded-md font-label text-white hover:bg-white/10 hover:text-white",
            "data-[active=true]:bg-white data-[active=true]:font-medium data-[active=true]:text-brand-secondary",
            "data-[active=true]:hover:bg-white data-[active=true]:hover:text-brand-secondary",
            "group-data-[collapsible=icon]:size-[35px]! group-data-[collapsible=icon]:p-2!",
          )

          const label = (
            <>
              <Icon className="size-5 shrink-0" />
              <span className={cn("flex-1 truncate", sidebarCollapseLabelClassName)}>
                {item.title}
              </span>
              {item.badge ? (
                <span className={cn("min-w-5 rounded-full bg-white px-1.5 text-center text-xs font-medium text-brand-secondary", sidebarCollapseLabelClassName)}>
                  {item.badge}
                </span>
              ) : null}
            </>
          )

          if (!hasChildren) {
            return (
              <SidebarMenuItem key={item.title} className="group/menu-item">
                <SidebarMenuButton
                  asChild
                  tooltip={item.title}
                  isActive={active}
                  className={buttonClassName}
                >
                  <Link to={item.url} className="flex w-full items-center gap-2.5 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:gap-0">
                    {label}
                    <SidebarChevronIcon
                      className={cn(
                        sidebarCollapseChevronClassName,
                        "text-current opacity-80",
                      )}
                    />
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            )
          }

          return (
            <Collapsible
              key={item.title}
              asChild
              defaultOpen={active}
              className="group/collapsible"
            >
              <SidebarMenuItem className="group/menu-item">
                <SidebarMenuButton
                  asChild
                  tooltip={item.title}
                  isActive={active}
                  className={buttonClassName}
                >
                  {goToMainOnParentClick ? (
                    <Link
                      to={getMainSubItemUrl(item)}
                      className="flex w-full items-center gap-2.5 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:gap-0"
                    >
                      {label}
                      <SidebarChevronIcon
                        className={cn(
                          sidebarCollapseChevronClassName,
                          "text-current opacity-80",
                        )}
                      />
                    </Link>
                  ) : (
                    <CollapsibleTrigger>
                      {label}
                      <SidebarChevronIcon
                        className={cn(
                          sidebarCollapseChevronClassName,
                          "text-current opacity-80 transition-transform",
                          "group-data-[state=open]/collapsible:rotate-90",
                        )}
                      />
                    </CollapsibleTrigger>
                  )}
                </SidebarMenuButton>
                <CollapsibleContent>
                  <SidebarMenuSub className="mx-0 border-l border-white/20 px-2">
                    {item.items?.map((subItem) => (
                      <SidebarMenuSubItem key={subItem.title}>
                        <SidebarMenuSubButton
                          asChild
                          isActive={pathMatchesSubItem(pathname, search, subItem.url)}
                          className="bg-transparent font-label text-white/80 hover:bg-white/10 hover:text-white active:bg-white/10 data-[active=true]:bg-white/15 data-[active=true]:text-white"
                        >
                          <Link to={subItem.url}>
                            <span>{subItem.title}</span>
                          </Link>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                    ))}
                  </SidebarMenuSub>
                </CollapsibleContent>
              </SidebarMenuItem>
            </Collapsible>
          )
        })}
      </SidebarMenu>
    </SidebarGroup>
  )
}
